import { Product } from "@/api/product-api";
import { useTrans } from "@/utils/useTrans";
import React, { Fragment } from "react";
import Container from "@/components/commons/Container";
import ProductsInTheBox from "@/components/tactsuit/components/ProductsInTheBox";
import TechSpecs from "@/components/tactsuit/tech-specs";
import SectionHeader from "@/components/tactsuit/common/SectionHeader";
import ProductCard from "@/v2-components/ProductCard";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { CompareProductsBody } from "@/components/tactsuit/product-comparison/Comparison";
import TactSuitCard from "@/v2-components/TactSuitCard";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { useInTheBoxProducts } from "@/components/tactsuit/components/useInTheBoxProducts";

export default function TactsuitTechSpecs({
  product,
  deals,
  useComparator = false,
  compareProducts,
  previewSlug,
  specsNote,
  boxProduct,
}: {
  useComparator?: boolean;
  className?: string;
  product: Product;
  deals: Product[];
  compareProducts?: Product[]; // 비교 대상 제품
  /** 스펙 미리보기만 다른 데이터로(`TechSpecs` 의 `previewSlug`) — EXOSHOCK 번들 */
  previewSlug?: string;
  /** 스펙 미리보기 아래 안내(번역 키) */
  specsNote?: string;
  /** In the Box 를 이 제품 기준으로 — 스펙은 Air, 박스는 번들(EXOSHOCK 와펜 Air)일 때 */
  boxProduct?: Product;
}) {
  const existAccessories = deals?.length > 0;
  const { t } = useTrans(["tactsuit"]);
  const [open, setOpen] = React.useState(false);

  const boxProducts = useInTheBoxProducts(boxProduct ?? product);

  return (
    <Fragment>
      <Comparator
        open={open}
        product={product}
        products={compareProducts ?? []}
        onOpenChange={setOpen}
      />
      <Container className={"max-w-screen-mdl overflow-hidden"}>
        <TechSpecs
          slug={product.slug}
          previewSlug={previewSlug}
          note={specsNote}
          productName={product.name}
          className="!mt-0"
          useComparator={useComparator}
          onCompare={() => {
            setOpen(true);
          }}
        />
        {/* prod 와 같은 `In the Box` 섹션. 종전 구현(tech-specs/inthebox.json 기반)은
            `tactglove-dk3` 와 번들 키가 없어서 **DK3 PDP 에서 섹션이 통째로 안 나왔고**,
            없는 slug 는 폴백이 `{ name: "Hello" }` 를 그리게 돼 있었다(실측).
            `ProductInTheBoxData` 는 DK3·Metro·Arizona 를 갖고 번들 구성품 나열도 된다. */}
        <div className={existAccessories ? "" : "mb-16"}>
          <ProductsInTheBox products={boxProducts} />
        </div>
        {existAccessories && (
          <>
            {/* 모바일 간격은 피그마 28718:164279 기준 — 제목↔그리드 20, 행 24 / 열 16.
                laptop 은 기존 값 유지(데스크톱 대조 범위 밖). */}
            <SectionHeader
              className={
                "mb-5 mt-16 laptop:mb-0 laptop:mt-24 laptop:text-headline-lg"
              }
            >
              {t("Additional Accessories")}
            </SectionHeader>
            <div
              className={
                "grid grid-cols-2 gap-x-4 gap-y-6 laptop:my-5 laptop:grid-cols-4 laptop:gap-4"
              }
            >
              {deals.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </>
        )}
      </Container>
    </Fragment>
  );
}

const Comparator = ({
  open,
  product,
  products,
  onOpenChange,
}: {
  open: boolean;
  product: Product;
  products: Product[];
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobileView();
  const { t } = useTrans(["tactsuit"]);
  const slugs = products.map((p) => p.slug);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex h-[90vh] flex-col laptop:h-full laptop:min-w-[634px]"
        side={isMobile ? "bottom" : "right"}
      >
        <SheetHeader className="items-center justify-between">
          <SheetTitle>{t("Compare")}</SheetTitle>
          <SheetClose className="focus:outline-none">
            <CrossIcon />
            <span className="sr-only">Close</span>
          </SheetClose>
        </SheetHeader>
        <div className="flex-1 overflow-auto">
          <div className="grid auto-cols-fr grid-flow-col gap-x-4 laptop:gap-x-7">
            <Card product={product} />
            <Card product={products[1]} />
          </div>
          <CompareProductsBody slugs={slugs} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Card = ({ product }: { product: Product }) => {
  return (
    <div className="mb-7 w-full select-none laptop:mb-24">
      <TactSuitCard product={product}>
        <div className="flex flex-col items-center gap-3 laptop:gap-5">
          <TactSuitCard.Name className="text-base laptop:text-lg" />
          <div
            className={
              "relative my-3 flex aspect-square w-full items-center justify-center laptop:w-[240px]"
            }
          >
            <TactSuitCard.Thumbnail fill />
          </div>
          <div className="h-[28px] laptop:h-[32px]">
            <TactSuitCard.MetaBadge className="h-full w-[127px] laptop:w-[145px]" />
          </div>
          <TactSuitCard.Price className="text-center text-base font-400 laptop:text-2xl" />
          <TactSuitCard.LearnMore className="pl-4" />
        </div>
      </TactSuitCard>
    </div>
  );
};
